import { Card } from "@/components/ui/card";
import { calculateUnits } from "./calculateUnits";
import { gql, useLazyQuery } from "@apollo/client";
import { Button } from "@/components/ui/button";
import React, { useMemo, useState } from "react";
import { DatePicker } from "@/components/ui/date-picker";
import { SearchIcon } from "lucide-react";
import { toast } from '@/components/ui/toast';
import { utils as xlsxUtils, writeFile } from 'xlsx';

export const GET_CALAIM_NOTES = gql`
  query GetCalaimNotes($startDate: Date, $endDate: Date) {
    calaimNotes(startDate: $startDate, endDate: $endDate) {
      id
      participant
      pos_code
      pos_name
      procedurecode
      proceduremodifier
      proceduremod1
      proceduremod2
      proceduremod3
      proceduretype
      procedurelength
      note_entry_date
      procedurenote
      procedureentery
      entrydate
      entryby
    }
  }
`;

// Helper function to get procedure code display
const getProcedureCodeDisplay = (note: any) => {
  const modifiers = [note.proceduremodifier, note.proceduremod1, note.proceduremod2, note.proceduremod3]
    .filter(Boolean)
    .join(' ');
  
  return `${note.procedurecode} ${modifiers}`.trim();
};

export const DateRangeNotes = () => {
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  const [fetchNotes, { data }] = useLazyQuery(GET_CALAIM_NOTES);

  const handleSearch = () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    fetchNotes({ 
      variables: {
        startDate: startDate || null,
        endDate: endDate || null
      }
    });
  };

  const handleReset = () => {
    setStartDate(undefined);
    setEndDate(undefined);
  };
  
  // Process notes to detect duplicates and calculate units
  const processedNotes = useMemo(() => {
    if (!data?.calaimNotes) return [];
    
    // Group notes by date and procedure code (not considering modifiers)
    const groupedNotes: { [key: string]: any[] } = {};
    data.calaimNotes.forEach((note: any) => {
      const date = new Date(note.note_entry_date).toLocaleDateString();
      // Just use the base procedure code (G9012, G9008, etc.) for grouping
      const procCode = note.procedurecode;
      const key = `${date}-${procCode}-${note.participant}`;
      
      if (!groupedNotes[key]) {
        groupedNotes[key] = [];
      }
      
      groupedNotes[key].push(note);
    });
    
    // Process each group to determine which notes are enabled/disabled
    const processedNotes: any[] = [];
    Object.entries(groupedNotes).forEach(([key, group]) => {
      // Calculate the total procedure length for this group
      const totalLength = group.reduce((sum, note) => sum + Number(note.procedurelength || 0), 0);
      
      // Calculate units based on the total length (calling calculateUnits just once per group)
      const totalUnits = calculateUnits(totalLength);
      
      // Sort by procedure length in descending order to prioritize notes with more length
      group.sort((a, b) => Number(b.procedurelength || 0) - Number(a.procedurelength || 0));
      
      // Mark the first note as enabled with total units, others as disabled
      const notesWithUnits = group.map((note, index) => ({
        ...note,
        isEnabled: index === 0,
        // For the enabled note, use the total units from the group calculation
        displayUnits: index === 0 ? totalUnits : 0
      }));
      
      processedNotes.push(...notesWithUnits);
    });
    
    // Sort processed notes by date
    return processedNotes.sort((a: any, b: any) => 
      new Date(a.note_entry_date).getTime() - new Date(b.note_entry_date).getTime()
    );
  }, [data?.calaimNotes]);

  // Use the processed notes directly since filtering is now done at the query level
  const filteredNotes = processedNotes;

  const handleExport = () => {
    if (!filteredNotes.length) return;

    const exportData = filteredNotes.map(note => ({
      Participant: note.participant,
      Date: new Date(note.note_entry_date).toLocaleDateString(),
      Procedure: getProcedureCodeDisplay(note),
      Length: note.procedurelength,
      Units: note.isEnabled ? note.displayUnits : '',
      Notes: note.procedurenote,
      'Entry Date': new Date(note.entrydate).toLocaleDateString(),
      'Entry By': note.entryby
    }));

    const ws = xlsxUtils.json_to_sheet(exportData);
    const wb = xlsxUtils.book_new();
    xlsxUtils.book_append_sheet(wb, ws, 'Notes');
    writeFile(wb, 'billing-notes.xlsx');
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-row gap-4 items-end">
        <DatePicker date={startDate} setDate={setStartDate} label="Start Date" />
        <DatePicker date={endDate} setDate={setEndDate} label="End Date" />
        <Button 
          onClick={handleSearch} 
          className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white"
        >
          <SearchIcon size={16} />
          Search
        </Button>
        <Button 
          onClick={handleReset} 
          variant="outline"
        >
          Reset
        </Button>
        <Button
          onClick={handleExport}
          variant="outline"
          disabled={!filteredNotes.length}
          className="ml-auto"
        >
          Export Excel
        </Button>
      </div>
      
      <h2 className="text-lg font-semibold">All Notes</h2>
      
      {!data && (
        <div className="p-4 text-center text-muted-foreground">
          Set date range and click Search to view notes
        </div>
      )}
      
      {data && filteredNotes.length === 0 && (
        <div className="p-4 text-center text-muted-foreground">
          No notes found for the selected date range
        </div>
      )}
      
      {filteredNotes.length > 0 && (
        <div className="space-y-0.5">
          <div className="grid grid-cols-[80px_80px_180px_60px_60px_1fr_80px_100px] gap-3 text-xs font-semibold text-muted-foreground px-2 pb-2">
            <span>Participant</span>
            <span>Date</span>
            <span>Procedure</span>
            <span>Length</span>
            <span>Units</span>
            <span>Notes</span>
            <span>Entry Date</span>
            <span>Entry By</span>
          </div>
          
          {filteredNotes.map((note) => (
            <Card 
              key={note.id} 
              className={`p-2 border-b border-t-0 border-x-0 rounded-none ${
                note.isEnabled ? "bg-transparent" : "bg-muted/50 opacity-40"
              }`}
            >
              <div className="grid grid-cols-[80px_80px_180px_60px_60px_1fr_80px_100px] gap-3 text-xs">
                <span>{note.participant}</span>
                <span>{new Date(note.note_entry_date).toLocaleDateString()}</span>
                <span>{getProcedureCodeDisplay(note)}</span>
                <span>{note.procedurelength}</span>
                <span>{note.isEnabled ? note.displayUnits : ''}</span>
                <span>{note.procedurenote}</span>
                <span>{new Date(note.entrydate).toLocaleDateString()}</span>
                <span>{note.entryby}</span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
